import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  IconButton,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormGroup
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoIcon,
  AttachFile as AttachFileIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import inventoryApi from '../../../services/inventoryApi';

const InspectionDialog = ({ open, onClose, grn, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Inspection data
  const [inspectionData, setInspectionData] = useState({
    status: 'in_progress',
    inspector: '',
    inspectionDate: new Date().toISOString().split('T')[0],
    overallResult: '',
    remarks: '',
    checkpoints: [],
    lineItemResults: []
  });

  // Predefined checkpoints
  const [availableCheckpoints] = useState([
    { parameter: 'Visual Inspection', required: true },
    { parameter: 'Quantity Verification', required: true },
    { parameter: 'Packaging Condition', required: true },
    { parameter: 'Label Accuracy', required: false },
    { parameter: 'Expiry Date Check', required: false },
    { parameter: 'Batch/Serial Verification', required: false },
    { parameter: 'Temperature Check', required: false },
    { parameter: 'Weight Verification', required: false },
    { parameter: 'Dimension Check', required: false },
    { parameter: 'Color Specification', required: false },
    { parameter: 'Functionality Test', required: false },
    { parameter: 'Documentation Complete', required: false }
  ]);

  const resultOptions = [
    { value: 'pass', label: 'Pass', color: 'success' },
    { value: 'fail', label: 'Fail', color: 'error' },
    { value: 'conditional', label: 'Conditional', color: 'warning' }
  ];

  useEffect(() => {
    if (grn && open) {
      initializeInspection();
    }
  }, [grn, open]);

  const initializeInspection = () => {
    // Initialize checkpoints based on existing data or defaults
    const existingCheckpoints = grn.qualityInspection?.checkpoints || [];
    const defaultCheckpoints = availableCheckpoints
      .filter(cp => cp.required)
      .map(cp => ({
        parameter: cp.parameter,
        result: '',
        remarks: '',
        critical: cp.required
      }));

    // Initialize line item results
    const lineItemResults = grn.lineItems?.map(item => ({
      itemId: item._id || item.item?._id,
      itemName: item.item?.name,
      orderedQuantity: item.orderedQuantity,
      receivedQuantity: item.receivedQuantity,
      acceptedQuantity: item.receivedQuantity,
      rejectedQuantity: 0,
      defects: [],
      inspectionResult: 'pass',
      remarks: ''
    })) || [];

    setInspectionData({
      status: grn.qualityInspection?.status || 'in_progress',
      inspector: grn.qualityInspection?.inspector?._id || '',
      inspectionDate: grn.qualityInspection?.inspectionDate 
        ? new Date(grn.qualityInspection.inspectionDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      overallResult: grn.qualityInspection?.overallResult || '',
      remarks: grn.qualityInspection?.remarks || '',
      checkpoints: existingCheckpoints.length > 0 ? existingCheckpoints : defaultCheckpoints,
      lineItemResults: grn.qualityInspection?.lineItemResults || lineItemResults
    });
  };

  const handleInputChange = (field, value) => {
    setInspectionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckpointChange = (index, field, value) => {
    setInspectionData(prev => ({
      ...prev,
      checkpoints: prev.checkpoints.map((checkpoint, i) => 
        i === index ? { ...checkpoint, [field]: value } : checkpoint
      )
    }));
  };

  const addCheckpoint = (parameter) => {
    setInspectionData(prev => ({
      ...prev,
      checkpoints: [...prev.checkpoints, {
        parameter,
        result: '',
        remarks: '',
        critical: false
      }]
    }));
  };

  const removeCheckpoint = (index) => {
    setInspectionData(prev => ({
      ...prev,
      checkpoints: prev.checkpoints.filter((_, i) => i !== index)
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    setInspectionData(prev => ({
      ...prev,
      lineItemResults: prev.lineItemResults.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));

    // Auto-calculate accepted/rejected quantities
    if (field === 'acceptedQuantity' || field === 'rejectedQuantity') {
      const lineItem = inspectionData.lineItemResults[index];
      const receivedQuantity = lineItem.receivedQuantity;
      
      if (field === 'acceptedQuantity') {
        const acceptedQuantity = parseInt(value) || 0;
        const rejectedQuantity = Math.max(0, receivedQuantity - acceptedQuantity);
        
        setInspectionData(prev => ({
          ...prev,
          lineItemResults: prev.lineItemResults.map((item, i) => 
            i === index ? { 
              ...item, 
              acceptedQuantity,
              rejectedQuantity,
              inspectionResult: rejectedQuantity > 0 ? 'conditional' : 'pass'
            } : item
          )
        }));
      }
    }
  };

  const calculateOverallResult = () => {
    const checkpointResults = inspectionData.checkpoints.map(cp => cp.result);
    const lineItemResults = inspectionData.lineItemResults.map(li => li.inspectionResult);
    
    const allResults = [...checkpointResults, ...lineItemResults];
    
    if (allResults.some(result => result === 'fail')) {
      return 'fail';
    } else if (allResults.some(result => result === 'conditional')) {
      return 'conditional';
    } else if (allResults.every(result => result === 'pass')) {
      return 'pass';
    }
    return '';
  };

  const validateInspection = () => {
    const errors = [];
    
    if (!inspectionData.inspector) {
      errors.push('Inspector is required');
    }
    
    if (!inspectionData.inspectionDate) {
      errors.push('Inspection date is required');
    }
    
    // Check if all required checkpoints have results
    const requiredCheckpoints = inspectionData.checkpoints.filter(cp => cp.critical);
    const incompleteRequired = requiredCheckpoints.filter(cp => !cp.result);
    if (incompleteRequired.length > 0) {
      errors.push(`Please complete all required checkpoints: ${incompleteRequired.map(cp => cp.parameter).join(', ')}`);
    }
    
    // Check if all line items have been reviewed
    const incompleteLineItems = inspectionData.lineItemResults.filter(li => !li.inspectionResult);
    if (incompleteLineItems.length > 0) {
      errors.push('Please complete inspection for all line items');
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateInspection();
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join('. '));
      return;
    }

    try {
      setLoading(true);

      // Calculate overall result if not manually set
      const overallResult = inspectionData.overallResult || calculateOverallResult();

      const inspectionPayload = {
        ...inspectionData,
        overallResult,
        status: 'completed'
      };

      await inventoryApi.completeGRNInspection(grn._id, inspectionPayload);
      onSuccess();
    } catch (err) {
      console.error('Error completing inspection:', err);
      toast.error(err?.response?.data?.message || 'Failed to complete inspection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'pass':
        return <CheckCircleIcon color="success" />;
      case 'fail':
        return <CancelIcon color="error" />;
      case 'conditional':
        return <WarningIcon color="warning" />;
      default:
        return null;
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'pass':
        return 'success';
      case 'fail':
        return 'error';
      case 'conditional':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (!grn) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon />
          <Typography variant="h5">
            Quality Inspection - {grn.grnNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Basic Inspection Information */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Inspection Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Inspector"
                    value={inspectionData.inspector}
                    onChange={(e) => handleInputChange('inspector', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Inspection Date"
                    value={inspectionData.inspectionDate}
                    onChange={(e) => handleInputChange('inspectionDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Overall Result"
                    value={inspectionData.overallResult}
                    onChange={(e) => handleInputChange('overallResult', e.target.value)}
                  >
                    <MenuItem value="">Auto-Calculate</MenuItem>
                    {resultOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getResultIcon(option.value)}
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Typography variant="body2">Status:</Typography>
                    <Chip
                      label={inspectionData.status?.replace('_', ' ').toUpperCase()}
                      color={inspectionData.status === 'completed' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="General Remarks"
                    value={inspectionData.remarks}
                    onChange={(e) => handleInputChange('remarks', e.target.value)}
                    placeholder="Overall inspection remarks..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Inspection Checkpoints */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Inspection Checkpoints
                </Typography>
                <TextField
                  select
                  size="small"
                  label="Add Checkpoint"
                  onChange={(e) => {
                    addCheckpoint(e.target.value);
                    e.target.value = '';
                  }}
                  sx={{ minWidth: 200 }}
                >
                  {availableCheckpoints
                    .filter(cp => !inspectionData.checkpoints.some(existing => existing.parameter === cp.parameter))
                    .map((checkpoint) => (
                      <MenuItem key={checkpoint.parameter} value={checkpoint.parameter}>
                        {checkpoint.parameter}
                      </MenuItem>
                    ))}
                </TextField>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Parameter</TableCell>
                      <TableCell align="center">Result</TableCell>
                      <TableCell>Remarks</TableCell>
                      <TableCell align="center">Critical</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inspectionData.checkpoints.map((checkpoint, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={checkpoint.critical ? 'bold' : 'normal'}>
                            {checkpoint.parameter}
                            {checkpoint.critical && (
                              <Chip label="Required" size="small" color="warning" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <FormControl>
                            <RadioGroup
                              row
                              value={checkpoint.result}
                              onChange={(e) => handleCheckpointChange(index, 'result', e.target.value)}
                            >
                              {resultOptions.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={<Radio size="small" />}
                                  label={getResultIcon(option.value)}
                                  sx={{ mr: 1 }}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Remarks..."
                            value={checkpoint.remarks}
                            onChange={(e) => handleCheckpointChange(index, 'remarks', e.target.value)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={checkpoint.critical}
                            onChange={(e) => handleCheckpointChange(index, 'critical', e.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => removeCheckpoint(index)}
                            disabled={checkpoint.critical}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Line Item Inspection */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Item-wise Inspection Results
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="center">Received</TableCell>
                      <TableCell align="center">Accepted</TableCell>
                      <TableCell align="center">Rejected</TableCell>
                      <TableCell align="center">Result</TableCell>
                      <TableCell>Defects/Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inspectionData.lineItemResults.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {item.itemName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Ordered: {item.orderedQuantity}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {item.receivedQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            size="small"
                            type="number"
                            value={item.acceptedQuantity}
                            onChange={(e) => handleLineItemChange(index, 'acceptedQuantity', e.target.value)}
                            sx={{ width: 80 }}
                            inputProps={{ min: 0, max: item.receivedQuantity }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            color={item.rejectedQuantity > 0 ? 'error.main' : 'text.primary'}
                          >
                            {item.rejectedQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <FormControl>
                            <RadioGroup
                              row
                              value={item.inspectionResult}
                              onChange={(e) => handleLineItemChange(index, 'inspectionResult', e.target.value)}
                            >
                              {resultOptions.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={<Radio size="small" />}
                                  label={getResultIcon(option.value)}
                                  sx={{ mr: 1 }}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Defects or remarks..."
                            value={item.remarks}
                            onChange={(e) => handleLineItemChange(index, 'remarks', e.target.value)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Inspection Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Checkpoints
                  </Typography>
                  <Typography variant="h6">
                    {inspectionData.checkpoints.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Completed Checkpoints
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {inspectionData.checkpoints.filter(cp => cp.result).length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Overall Result
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getResultIcon(inspectionData.overallResult || calculateOverallResult())}
                    <Chip
                      label={(inspectionData.overallResult || calculateOverallResult() || 'Pending').toUpperCase()}
                      color={getResultColor(inspectionData.overallResult || calculateOverallResult())}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Completion Status
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {Math.round((inspectionData.checkpoints.filter(cp => cp.result).length / Math.max(inspectionData.checkpoints.length, 1)) * 100)}%
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={<AssessmentIcon />}
        >
          {loading ? 'Completing...' : 'Complete Inspection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InspectionDialog;